import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StarsInput } from "@/components/Stars";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import { CheckCircle2 } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function AvisFound() {
  const { foundId } = useParams();
  const [params] = useSearchParams();
  const session = params.get("s") || "";
  const role = params.get("r") || "";
  const { t } = useLang();
  const [ctx, setCtx] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api(`/reviews/found-context/${foundId}?s=${encodeURIComponent(session)}${role ? `&r=${encodeURIComponent(role)}` : ""}`, { auth: false })
      .then((c) => { setCtx(c); if (c.already_reviewed) setDone(true); })
      .catch((e) => setLoadErr(e.message));
  }, [foundId, session, role]);

  async function submit(e) {
    e.preventDefault();
    if (!stars) return toast.error(t("rate.chooseNote"));
    setBusy(true);
    try {
      await api("/reviews", {
        method: "POST", auth: false,
        body: {
          kind: "b2c", linked_id: foundId, session_id: session,
          author_role: "claimant",
          author_name: ctx?.claimant_name || "Client findloop",
          author_email: ctx?.claimant_email,
          rating: stars,
          title: (comment.trim().slice(0, 60)) || "Merci findloop",
          body: comment.trim() || "Objet restitué avec succès.",
        },
      });
      setDone(true);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  if (loadErr) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="avis-error">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">{t("rate.title")}</h1>
        <p className="mt-3 text-red-600">{loadErr}</p>
        <Link to="/"><Button className="mt-6 rounded-xl font-semibold">{t("rate.home")}</Button></Link>
      </div>
    );
  }
  if (!ctx && !done) {
    return <div className="max-w-md mx-auto px-6 py-24 text-center"><FindoSpinner size={24} className="mx-auto" /></div>;
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16 text-center">
      {done ? (
        <div className="animate-fade-up" data-testid="avis-success">
          <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-teal-100 text-teal-700"><CheckCircle2 size={34} /></div>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">{t("rate.successTitle")}</h1>
          <p className="mt-2 text-slate-600">{t("rate.successText")}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/"><Button className="rounded-xl font-semibold w-full sm:w-auto" data-testid="avis-home-btn">{t("rate.home")}</Button></Link>
            <Link to="/mon-compte"><Button variant="outline" className="rounded-xl font-semibold w-full sm:w-auto" data-testid="avis-account-btn">{t("mc.title") || "Mon compte"}</Button></Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="avis-form">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{t("rate.title")}</h1>
          {ctx?.object_photo && (
            <img src={ctx.object_photo} alt={ctx?.object || ""} data-testid="avis-object-photo"
              className="mt-5 mx-auto h-40 w-40 rounded-2xl object-cover border border-border card-shadow" />
          )}
          <p className="mt-3 text-slate-600">{ctx?.object ? `« ${ctx.object} »` : t("rate.subtitle")}</p>
          <div className="mt-8 flex justify-center"><StarsInput value={stars} onChange={setStars} /></div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={500}
            placeholder={t("rate.commentPh")} className="mt-6 rounded-xl resize-none text-left" data-testid="avis-comment" />
          <Button type="submit" disabled={busy} className="mt-4 w-full h-12 rounded-xl font-semibold" data-testid="avis-submit">
            {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("rate.submitting")}</span> : t("rate.submit")}
          </Button>
        </form>
      )}
    </div>
  );
}
